/**
 * Diagnostico de navegador in-app (Instagram/Facebook) -- SO NA SANDBOX.
 *
 * Mede, no proprio aparelho, o que costuma quebrar nesses WebViews: fetch
 * embrulhado por scripts injetados, URL relativa recusada, FontFace, storage do
 * iframe, origem do pai (referrer x ancestorOrigins) e a troca de mensagens com
 * a loja. Nada e enviado ao servidor: o resultado aparece num painel com botao
 * de copiar. Liga sozinho em navegador in-app; em outros, com ?tb_diag=1.
 */

type Linha = { k: string; v: string; ruim?: boolean };

const IAB_RE = /Instagram|FBAN|FBAV|FB_IAB|FBIOS|Messenger|musical_ly|TikTok|BytedanceWebview|Line\//i;

function nativo(fn: unknown): string {
  try { return /\[native code\]/.test(Function.prototype.toString.call(fn)) ? 'nativo' : 'EMBRULHADO'; }
  catch { return 'indefinido'; }
}

function erroTexto(e: unknown): string {
  if (!e) return 'erro';
  const x = e as { name?: string; message?: string };
  return `${x.name || 'Error'}: ${String(x.message || e).slice(0, 140)}`;
}

async function comTempo<T>(p: Promise<T>, ms: number): Promise<T> {
  return Promise.race([p, new Promise<T>((_, rej) => setTimeout(() => rej(new Error(`timeout ${ms}ms`)), ms))]);
}

export function iabDiagEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  if (!/^sandbox\.turbine\.paccoby\.com\.br$|^localhost$|^127\.0\.0\.1$/.test(window.location.hostname)) return false;
  const p = new URLSearchParams(window.location.search).get('tb_diag');
  if (p === '0') return false;
  return p === '1' || IAB_RE.test(navigator.userAgent || '');
}

const erros: string[] = [];

export function startIabDiag(): void {
  if (!iabDiagEnabled()) return;
  window.addEventListener('error', (e) => { if (erros.length < 25) erros.push(`error: ${String(e.message).slice(0, 140)} @${String(e.filename || '').split('/').pop()}:${e.lineno}`); }, true);
  window.addEventListener('error', (e) => {
    const t = e.target as HTMLElement | null;
    if (t && t !== (window as unknown) && (t as HTMLImageElement).src && erros.length < 25) erros.push(`recurso falhou: ${(t as HTMLImageElement).src.slice(0, 120)}`);
  }, true);
  window.addEventListener('unhandledrejection', (e) => { if (erros.length < 25) erros.push(`promise: ${erroTexto(e.reason)}`); });
  const origWarn = console.warn.bind(console);
  console.warn = (...a: unknown[]) => { try { const s = a.map(String).join(' '); if (/Fonts|Failed|falh/i.test(s) && erros.length < 25) erros.push(`warn: ${s.slice(0, 160)}`); } catch { /* noop */ } origWarn(...a); };
  // Mensagens recebidas do pai (consentimento, cartSuccess/cartError...)
  const recebidas: string[] = [];
  window.addEventListener('message', (e) => {
    const t = e.data && typeof e.data === 'object' ? String((e.data as { type?: string }).type || '?') : typeof e.data;
    if (recebidas.length < 15) recebidas.push(`${t} de ${e.origin}${e.source === window.parent ? '' : ' (NAO e o pai)'}`);
  });
  setTimeout(() => { void rodar(recebidas); }, 6000);
}

async function rodar(recebidas: string[]): Promise<void> {
  const L: Linha[] = [];
  const add = (k: string, v: string, ruim = false) => L.push({ k, v, ruim });
  const ua = navigator.userAgent || '';
  add('versao', 'iab-diag v1');
  add('UA', ua);
  add('in-app', IAB_RE.test(ua) ? 'sim' : 'nao');
  add('em iframe', window.parent !== window ? 'sim' : 'nao');
  add('referrer', document.referrer || '(vazio)');
  let ancestor = '(sem API)';
  try { const ao = (location as Location & { ancestorOrigins?: DOMStringList }).ancestorOrigins; if (ao) ancestor = Array.from({ length: ao.length }, (_, i) => ao[i]).join(' > ') || '(vazio)'; } catch { /* noop */ }
  add('ancestorOrigins', ancestor);
  try {
    const ref = document.referrer ? new URL(document.referrer).origin : '';
    const pai = ancestor.split(' > ')[0];
    if (ref && pai.startsWith('http') && ref !== pai) add('ORIGEM DO PAI DIVERGE', `referrer=${ref} pai=${pai} -> postMessage do carrinho seria descartado`, true);
  } catch { /* noop */ }
  add('fetch', nativo(window.fetch), nativo(window.fetch) !== 'nativo');
  add('XHR.open', nativo(XMLHttpRequest.prototype.open), nativo(XMLHttpRequest.prototype.open) !== 'nativo');
  add('postMessage', nativo(window.parent.postMessage), false);
  add('FontFace', typeof FontFace === 'function' ? nativo(FontFace) : 'AUSENTE', typeof FontFace !== 'function');

  // Storage
  for (const [nome, get] of [['localStorage', () => window.localStorage], ['sessionStorage', () => window.sessionStorage]] as const) {
    try { const s = get(); s.setItem('__d', '1'); s.removeItem('__d'); add(nome, 'ok'); } catch (e) { add(nome, erroTexto(e), true); }
  }
  try { add('indexedDB', typeof indexedDB !== 'undefined' ? 'existe' : 'AUSENTE'); } catch (e) { add('indexedDB', erroTexto(e), true); }
  try { add('cookie', navigator.cookieEnabled ? 'habilitado' : 'DESABILITADO', !navigator.cookieEnabled); } catch (e) { add('cookie', erroTexto(e), true); }
  try { add('serviceWorker', 'serviceWorker' in navigator ? 'existe' : 'ausente'); } catch { /* noop */ }

  // Rede: relativa x absoluta, fetch x XHR
  const rel = '/api/public/fonts';
  const abs = `${window.location.origin}${rel}`;
  for (const [nome, url] of [['fetch relativa', rel], ['fetch absoluta', abs]] as const) {
    const t0 = performance.now();
    try { const r = await comTempo(fetch(url, { cache: 'no-store' }), 10000); add(nome, `HTTP ${r.status} em ${Math.round(performance.now() - t0)}ms`, !r.ok); }
    catch (e) { add(nome, erroTexto(e), true); }
  }
  await new Promise<void>((res) => {
    try {
      const x = new XMLHttpRequest(); x.open('GET', rel, true); x.timeout = 10000;
      x.onload = () => { add('XHR relativa', `HTTP ${x.status}`, x.status >= 400); res(); };
      x.onerror = () => { add('XHR relativa', 'falha de rede', true); res(); };
      x.ontimeout = () => { add('XHR relativa', 'timeout', true); res(); };
      x.send();
    } catch (e) { add('XHR relativa', erroTexto(e), true); res(); }
  });
  try {
    const r = await comTempo(fetch('/api/public/track', { method: 'OPTIONS' }), 8000);
    add('fetch POST/OPTIONS', `HTTP ${r.status}`);
  } catch (e) { add('fetch POST/OPTIONS', erroTexto(e), true); }

  // Fontes: FontFace relativa x absoluta, e o que ficou em document.fonts
  const fonte = '/uploads/fonts/Century.ttf';
  for (const [nome, url] of [['FontFace relativa', fonte], ['FontFace absoluta', `${window.location.origin}${fonte}`]] as const) {
    try { const f = await comTempo(new FontFace(`diag-${nome.length}`, `url('${url}')`).load(), 10000); add(nome, `ok (${f.status})`); }
    catch (e) { add(nome, erroTexto(e), true); }
  }
  try {
    const fams = Array.from(document.fonts as unknown as Iterable<FontFace>).map((f) => `${f.family}:${f.status}`).filter((s) => /turbine-font|Century|Lovely|Simplifica/.test(s));
    add('fontes do app', fams.length ? fams.join(', ') : 'NENHUMA carregada', fams.length === 0 || fams.some((s) => /error/.test(s)));
  } catch (e) { add('fontes do app', erroTexto(e), true); }

  // Imagem
  await new Promise<void>((res) => {
    const img = new Image(); const t = setTimeout(() => { add('imagem', 'timeout', true); res(); }, 10000);
    img.onload = () => { clearTimeout(t); add('imagem', `ok ${img.naturalWidth}x${img.naturalHeight}`); res(); };
    img.onerror = () => { clearTimeout(t); add('imagem', 'FALHOU', true); res(); };
    img.src = '/assets/images/logo-espera-personalize.png?d=' + Date.now();
  });

  // Mensagens com a loja: pede consentimento (a ponte da loja responde).
  if (window.parent !== window) {
    const resposta = new Promise<string>((res) => {
      const h = (e: MessageEvent) => { if (e.data && (e.data as { type?: string }).type === 'turbine:consent') { window.removeEventListener('message', h); res(`respondeu de ${e.origin}`); } };
      window.addEventListener('message', h); setTimeout(() => res('SEM RESPOSTA em 5s'), 5000);
    });
    let alvo = '*'; try { alvo = document.referrer ? new URL(document.referrer).origin : '*'; } catch { /* noop */ }
    try { window.parent.postMessage({ type: 'turbine:consentRequest' }, alvo); add('postMessage ao pai', `enviado para ${alvo}`); }
    catch (e) { add('postMessage ao pai', erroTexto(e), true); }
    const r = await resposta; add('resposta da loja', r, /SEM/.test(r));
  }
  add('mensagens recebidas', recebidas.join(' | ') || '(nenhuma)');
  add('erros capturados', erros.length ? erros.join(' || ') : '(nenhum)', erros.length > 0);
  mostrar(L);
}

function mostrar(L: Linha[]): void {
  const texto = L.map((l) => `${l.ruim ? '!! ' : ''}${l.k}: ${l.v}`).join('\n');
  (window as unknown as { __iabDiag?: string }).__iabDiag = texto;
  const box = document.createElement('div');
  box.setAttribute('data-clarity-mask', 'true');
  box.style.cssText = 'position:fixed;left:8px;right:8px;bottom:8px;max-height:55vh;overflow:auto;z-index:2147483647;background:#111;color:#eee;font:11px/1.35 ui-monospace,Menlo,monospace;padding:10px;border-radius:8px;box-shadow:0 4px 18px rgba(0,0,0,.4)';
  const topo = document.createElement('div');
  topo.style.cssText = 'display:flex;gap:8px;margin-bottom:6px;position:sticky;top:0;background:#111';
  const titulo = document.createElement('b'); titulo.textContent = `Diagnostico in-app (${L.filter((l) => l.ruim).length} problemas)`; titulo.style.flex = '1';
  const copiar = document.createElement('button'); copiar.textContent = 'Copiar';
  const fechar = document.createElement('button'); fechar.textContent = 'Fechar';
  for (const b of [copiar, fechar]) b.style.cssText = 'font:12px sans-serif;padding:4px 10px;border-radius:6px;border:0;background:#eee;color:#111';
  copiar.onclick = async () => {
    try { await navigator.clipboard.writeText(texto); copiar.textContent = 'Copiado'; }
    catch { const ta = document.createElement('textarea'); ta.value = texto; box.appendChild(ta); ta.select(); try { document.execCommand('copy'); copiar.textContent = 'Copiado'; } catch { copiar.textContent = 'Selecione e copie'; } }
  };
  fechar.onclick = () => box.remove();
  topo.append(titulo, copiar, fechar);
  const pre = document.createElement('pre'); pre.style.cssText = 'white-space:pre-wrap;word-break:break-all;margin:0';
  for (const l of L) { const s = document.createElement('div'); s.textContent = `${l.k}: ${l.v}`; if (l.ruim) s.style.color = '#ff8a7a'; pre.appendChild(s); }
  box.append(topo, pre);
  document.body.appendChild(box);
}
